import { Fragment, useState } from 'react';
import { Dialog, Transition, DialogPanel, DialogTitle, TransitionChild, RadioGroup } from '@headlessui/react';
import { FormInput, Button } from '../../components';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken } from '../../slices/staffSlice';
import { fetchRequests } from '../../Requests/getRequests';
import { show } from '../../slices/toastSlice';
import { authStaff, dateToHumanFormat } from '../../utils/constants';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { MoreHorizontalCircle01Icon, CheckmarkCircle01Icon, Cancel01Icon } from '@hugeicons/core-free-icons';

function PaymentItem({ item }) {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [transID, setTransID] = useState("");
    const [date, setDate] = useState("");
    const [amount, setAmount] = useState("");
    const [phone, setPhone] = useState("");
    const [penalRateAmount, setPenalRateAmount] = useState("");
    const [remarks, setRemarks] = useState("");
    const [process, setProcess] = useState(false);
    const [loans, setLoans] = useState([]);
    const [selectedLoan, setSelectedLoan] = useState();

    const handleAuthError = () => {
        sessionStorage.clear();
        navigate("/login");
        dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
    };

    const verifyLoan = async (e) => {
        e.preventDefault();
        setProcess(true);
        const res = await fetchRequests(`/loans/staff/search-loans`, "get", authStaff(accessToken), { state: "active", phone }, {});
        setProcess(false);
        if (res?.code === 401) return handleAuthError();
        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res?.err }));
            return;
        }
        setLoans(res);
    };

    const submitRequest = async (e) => {
        e.preventDefault();
        setProcess(true);
        const data = {
            _id: selectedLoan,
            msgId: item?._id,
            transID: transID.replace(/[^a-zA-Z0-9]/g, ''),
            date, amount, penalRateAmount, remarks
        };
        const res = await fetchRequests(`/payments/staff/match-payment`, "POST", authStaff(accessToken), {}, data);
        setProcess(false);
        if (res?.code === 401) return handleAuthError();
        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res?.err }));
            return;
        }
        dispatch(show({ type: "success", state: true, message: `${res?.message}. Reloading...` }));
        setTimeout(() => window.location.reload(), 2000);
    };

    const isMatched = !item?.unmatched;

    return (
        <>
        <tr className="hover:bg-gray-50/40 dark:hover:bg-gray-700 transition-colors">
            <td className="px-5 py-4 text-sm text-gray-500 dark:text-gray-100 whitespace-nowrap align-top">
                {dateToHumanFormat(item?.date)}
            </td>
            <td className="px-5 py-4 text-sm text-gray-500 dark:text-gray-100  align-top leading-relaxed">
                {item?.msg}
            </td>
            <td className="px-5 py-4 align-top">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold border ${
                    isMatched
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                        : 'bg-amber-50 text-amber-700 border-amber-100'
                }`}>
                    <HugeiconsIcon
                        icon={isMatched ? CheckmarkCircle01Icon : Cancel01Icon}
                        size={11}
                    />
                    {isMatched ? 'Matched' : 'Unmatched'}
                </span>
            </td>
            <td className="px-5 py-4 align-top">
                {!isMatched && (
                    <button
                        type="button"
                        onClick={() => setIsOpen(true)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Match payment"
                    >
                        <HugeiconsIcon icon={MoreHorizontalCircle01Icon} size={20} />
                    </button>
                )}
            </td>
        </tr>

        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
                <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </TransitionChild>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                            <DialogPanel className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                                {/* Modal header */}
                                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                                    <div>
                                        <DialogTitle className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                                            Match Payment
                                        </DialogTitle>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{dateToHumanFormat(item?.date)}</p>
                                    </div>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <HugeiconsIcon icon={Cancel01Icon} size={15} />
                                    </button>
                                </div>

                                {/* Transaction message */}
                                <div className="px-6 py-4 bg-gray-50/60 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
                                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5">Transaction Message</p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{item?.msg}</p>
                                </div>

                                {/* Form */}
                                <div className="px-6 py-5">
                                    <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <FormInput
                                            label="Transaction ID"
                                            type="text"
                                            name="transID"
                                            id="transID"
                                            value={transID}
                                            setValue={setTransID}
                                        />

                                        <div>
                                            <label htmlFor="date" className="form-label">Transaction Date</label>
                                            <input
                                                type="date"
                                                name="date"
                                                id="date"
                                                className="form-input"
                                                max={new Date().toISOString().split('T')[0]}
                                                value={date}
                                                onChange={(e) => setDate(e.target.value)}
                                                required
                                            />
                                        </div>

                                        <FormInput
                                            label="Amount"
                                            type="number"
                                            name="amount"
                                            id="amount"
                                            value={amount}
                                            setValue={setAmount}
                                        />

                                        <FormInput
                                            label="Client's Phone Number"
                                            type="text"
                                            name="phone"
                                            id="phone"
                                            value={phone}
                                            setValue={setPhone}
                                        />

                                        <FormInput
                                            label="Penal Rate Amount (0 if none)"
                                            type="number"
                                            name="penalRateAmount"
                                            id="penalRateAmount"
                                            value={penalRateAmount}
                                            setValue={setPenalRateAmount}
                                        />

                                        <FormInput
                                            label="Remarks"
                                            type="text"
                                            name="remarks"
                                            id="remarks"
                                            value={remarks}
                                            setValue={setRemarks}
                                        />

                                        {loans && loans.length > 0 && (
                                            <div className="col-span-full space-y-2">
                                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Select Loan</p>
                                                <RadioGroup value={selectedLoan} onChange={setSelectedLoan} className="space-y-2">
                                                    {loans.map((loan) => (
                                                        <RadioGroup.Option key={loan._id} value={loan._id}>
                                                            {({ checked }) => (
                                                                <div className={`flex items-center justify-between rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                                                                    checked
                                                                        ? 'border-[#1e3a6e] dark:border-blue-500 bg-blue-50/60 dark:bg-blue-500/10'
                                                                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                                }`}>
                                                                    <div>
                                                                        <p className={`text-sm font-medium ${checked ? 'text-[#1e3a6e] dark:text-blue-400' : 'text-gray-800 dark:text-gray-100'}`}>
                                                                            {loan?.signedName} &middot; {loan.type}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                                            Amount: {loan?.amountReq}gh &middot; Monthly: {loan?.accountInfo?.premium}gh
                                                                        </p>
                                                                    </div>
                                                                    {checked && (
                                                                        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={18} className="text-[#1e3a6e] shrink-0" />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </RadioGroup.Option>
                                                    ))}
                                                </RadioGroup>
                                            </div>
                                        )}

                                        <div className="col-span-full pt-2">
                                            {loans && loans.length > 0 ? (
                                                <Button
                                                    btnText="Submit"
                                                    btnClasses="form-button w-auto px-8"
                                                    btnType="submit"
                                                    btnFunction={submitRequest}
                                                    processing={process}
                                                />
                                            ) : (
                                                <Button
                                                    btnText="Verify Loan"
                                                    btnClasses="form-button w-auto px-8"
                                                    btnType="submit"
                                                    btnFunction={verifyLoan}
                                                    processing={process}
                                                />
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
        </>
    );
}

export default PaymentItem;
